import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Popconfirm, Space, Table, Tooltip } from "antd";
import { MessageOutlined, SearchOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import message from "../../message";
import UserChatsModal from "./UserChatsModal";

export default function UsersPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [account, setAccount] = useState("");
  const [password, setPassword] = useState("");
  const [maxActCount, setMaxActCount] = useState("");
  const [keyword, setKeyword] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const [chatUser, setChatUser] = useState(null);
  const [showChatButton, setShowChatButton] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8080";
  const token = useMemo(() => localStorage.getItem("token"), []);

  const loadList = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const resp = await fetch(`${baseUrl}/manager/user/list`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.toast(msg || "加载失败");
        return;
      }
      setList(result?.data?.list || []);
      setShowChatButton(Boolean(result?.data?.showChatButton));
    } catch (error) {
      message.toast("加载失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  useEffect(() => {
    if (location.state?.openAdd) {
      openAdd();
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);

  const openAdd = () => {
    setEditingId("");
    setAccount("");
    setPassword("");
    setMaxActCount("");
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setAccount(record.account || "");
    setPassword(record.password || "");
    setMaxActCount(String(record.maxActCount ?? ""));
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (saving) return;
    if (!account.trim()) {
      message.toast("请输入账号");
      return;
    }
    if (!password.trim()) {
      message.toast("请输入密码");
      return;
    }
    if (!String(maxActCount).trim()) {
      message.toast("请输入WhatsApp账号数量");
      return;
    }
    setSaving(true);
    try {
      const params = new URLSearchParams({ account, password, maxActCount });
      if (editingId) {
        params.set("id", editingId);
      }
      const endpoint = editingId ? "edit" : "add";
      const resp = await fetch(`${baseUrl}/manager/user/${endpoint}?${params}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.toast(msg || "添加失败");
        return;
      }
      message.success(editingId ? "保存成功" : "添加成功");
      setModalOpen(false);
      setEditingId("");
      setAccount("");
      setPassword("");
      setMaxActCount("");
      await loadList();
    } catch (error) {
      message.toast("添加失败");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const params = new URLSearchParams({ id: record.id });
      const resp = await fetch(`${baseUrl}/manager/user/delete?${params}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.toast(msg || "删除失败");
        return;
      }
      message.success("删除成功");
      setList((prev) => prev.filter((item) => item.id !== record.id));
    } catch (error) {
      message.toast("删除失败");
    }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      loadList();
      return;
    }
    if (loading) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ keyword });
      const resp = await fetch(`${baseUrl}/manager/user/search?${params}`, {
        headers: token ? { token } : {},
      });
      const result = await resp.json();
      const { code, msg } = result || {};
      if (code) {
        message.toast(msg || "搜索失败");
        return;
      }
      setList(result?.data?.list || []);
    } catch (error) {
      message.toast("搜索失败");
    } finally {
      setLoading(false);
    }
  };

  const openChat = (record) => {
    setChatUser(record);
    setChatOpen(true);
  };

  const columns = [
    {
      title: "账号",
      dataIndex: "account",
      key: "account",
      width: 220,
      align: "center",
      render: (value, record) => (
        <Space size={6}>
          <span>{value}</span>
          {showChatButton ? (
            <Tooltip title="查看聊天">
              <Button
                type="link"
                size="small"
                icon={<MessageOutlined />}
                onClick={() => openChat(record)}
                aria-label="查看聊天"
              />
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    { title: "密码", dataIndex: "password", key: "password", width: 160, align: "center" },
    { title: "WhatsApp账号上限", dataIndex: "maxActCount", key: "maxActCount", width: 180, align: "center" },
    { title: "今日会话数", dataIndex: "todayChatCount", key: "todayChatCount", width: 120, align: "center" },
    {
      title: "回复率",
      dataIndex: "replyRate",
      key: "replyRate",
      width: 120,
      align: "center",
      render: (value) => `${Number(value ?? 0) || 0}%`,
    },
    {
      title: "操作",
      key: "action",
      width: 140,
      align: "center",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除该用户？" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
        <Space>
          <Button type="primary" onClick={openAdd}>
            +添加账号
          </Button>
          <Button onClick={loadList} loading={loading}>
            刷新
          </Button>
        </Space>
        <Input.Search
          placeholder="搜索账号或ID"
          allowClear
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onSearch={handleSearch}
          enterButton={<Button type="default">搜索</Button>}
          prefix={<SearchOutlined />}
          style={{ maxWidth: 360, marginLeft: "auto" }}
        />
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        bordered
        pagination={{ pageSize: 10 }}
        tableLayout="fixed"
        className="manager-users-table"
      />
      <Modal
        title={editingId ? "编辑账号" : "添加账号"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setModalOpen(false)}>
            取消
          </Button>,
          <Button key="save" type="primary" loading={saving} onClick={handleSave}>
            保存
          </Button>,
        ]}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ marginBottom: 6, fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              账号
            </div>
            <Input
              placeholder="请输入账号"
              autoComplete="off"
              value={account}
              onChange={(event) => setAccount(event.target.value)}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              密码
            </div>
            <Input.Password
              placeholder="请输入密码"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <div>
            <div style={{ marginBottom: 6, fontSize: 14, color: "#6b7280" }}>
              <span style={{ color: "#ef4444", marginRight: 4 }}>*</span>
              WhatsApp账号数量
            </div>
            <Input
              placeholder="请输入WhatsApp账号数量"
              autoComplete="off"
              value={maxActCount}
              onChange={(event) => setMaxActCount(event.target.value)}
            />
          </div>
        </div>
      </Modal>
      <UserChatsModal
        open={chatOpen}
        user={chatUser}
        token={token}
        baseUrl={baseUrl}
        onClose={() => setChatOpen(false)}
      />
    </div>
  );
}
